import {useEffect} from 'react';

import {registerNavigationWarning} from 'lib/navigateToURL';

import usePrevious from './usePrevious';

/**
 * Warn the user with a confirm dialog if they try to leave or close the page.
 *
 * @param shouldWarn Whether the user should be warned
 * @param message Message to show in confirm dialog for internal navigations. Browser navigations show the default browser message.
 */
export default function useWarnOnNavigate(shouldWarn: boolean, message: string): void {
  useEffect(() => {
    if (!shouldWarn) {
      return;
    }

    // Block internal Backbone navigation
    const unregister = registerNavigationWarning(message);

    // Block browser navigation to another page
    function beforeunloadHandler(event: JQueryEventObject): string {
      event.preventDefault();
      return message;
    }
    window.addEventListener('beforeunload', beforeunloadHandler);

    return () => {
      unregister();
      window.removeEventListener('beforeunload', beforeunloadHandler);
    };
  }, [shouldWarn, message]);
  useWarnOnBackwardsHistoryNavigation(shouldWarn, message);
}

// exported for testing only
export enum BrowserNavigationHistoryState {
  MOVE_BACK_HOLDING = 'MOVE_BACK_HOLDING',
  STARTING_MARKER = 'STARTING_MARKER',
}

/*
  Interacting with the browser history stack is a bit complicated so leaving this long comment for future
  understanding.

  Note that this implementation clobbers the "forward" history. When a page using this hook is mounted, all
  browser history ahead of the current page is cleared in favor of the "dummy" states added to manage the
  confirmation behavior for backwards navigation.

  Currently we are at the head of the browser history stack.

  [page 0, page 1, current*]

  The * indicates where we are in the browser history

  When the user presses the back button the browser will immediately navigate to the previous page. (popstate
  fires after the state has been changed). With our current history setup, this will unmount the component
  and the listener won't fire.

  So we add a "dummy" state to the history stack and move the pointer to the end of the stack.

  Note that in code we call these states "MOVE_BACK_HOLDING" and "STARTING_MARKER".

  On mount:

  [page 0, page 1, MOVE_BACK_HOLDING, STARTING_MARKER*]

  Immediately after pressing the back button:

  [page 0, page 1, MOVE_BACK_HOLDING*, STARTING_MARKER]

  Then we can invoke the popstate handler. We immediately invoke "forward" to get back to the original state
  and then call "confirm" to validate whether the navigation was intentional.

  If confirmed:

  [page 0, page 1*, MOVE_BACK_HOLDING, STARTING_MARKER]

  Else:

  [page 0, page 1, MOVE_BACK_HOLDING, STARTING_MARKER*]
*/
export function useWarnOnBackwardsHistoryNavigation(shouldWarn: boolean, message: string): void {
  const previousShouldWarn = usePrevious(shouldWarn);

  useEffect(() => {
    const currentState = window.history.state;
    if (!shouldWarn) {
      const shouldWarnPopstateHandler = (event: PopStateEvent): void => {
        // If we land on one of our dummy states, we just go back to the previous state since we don't need
        // to warn the user.
        if (
          [
            BrowserNavigationHistoryState.MOVE_BACK_HOLDING,
            BrowserNavigationHistoryState.STARTING_MARKER,
          ].includes(event.state)
        ) {
          window.history.back();
        }
      };
      window.addEventListener('popstate', shouldWarnPopstateHandler);

      return () => {
        window.removeEventListener('popstate', shouldWarnPopstateHandler);
      };
    }

    // oxlint-disable-next-line no-explicit-any
    let currentPosition: any = BrowserNavigationHistoryState.STARTING_MARKER;
    let numInternalNavigationsQueueud = 0;

    const popstateHandler = (event: PopStateEvent): void => {
      if (numInternalNavigationsQueueud > 0) {
        numInternalNavigationsQueueud--;
        return;
      }
      const navigatingToMarker = event.state;
      const isNavigatingBack =
        currentPosition === BrowserNavigationHistoryState.STARTING_MARKER &&
        navigatingToMarker === BrowserNavigationHistoryState.MOVE_BACK_HOLDING;

      if (!isNavigatingBack) {
        currentPosition = navigatingToMarker;
        return;
      }

      numInternalNavigationsQueueud++;
      window.history.forward();

      setTimeout(() => {
        // oxlint-disable-next-line no-alert
        const confirmNavigation = window.confirm(message);
        if (confirmNavigation) {
          currentPosition = BrowserNavigationHistoryState.MOVE_BACK_HOLDING;
          numInternalNavigationsQueueud += 2;
          window.history.go(-2);
        } else {
          currentPosition = BrowserNavigationHistoryState.STARTING_MARKER;
        }
      });
    };

    window.addEventListener('popstate', popstateHandler);

    // Set up the history stack. We add a dummy back state and push to our starting marker.
    // We end up at STARTING_MARKER where we'll stay until the user navigates.
    // We only do this if we are not already at the starting marker to avoid cluttering the history when
    // shouldWarn toggles between true and false.
    if (currentState !== BrowserNavigationHistoryState.STARTING_MARKER) {
      window.history.replaceState(BrowserNavigationHistoryState.MOVE_BACK_HOLDING, '');
      window.history.pushState(BrowserNavigationHistoryState.STARTING_MARKER, '');
    }

    return () => {
      window.removeEventListener('popstate', popstateHandler);
    };
  }, [previousShouldWarn, shouldWarn, message]);
}
